import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../../api/clientInstance";
import { apiErrorMessage } from "../../utils/apiError";
import { useFriends } from "../../hooks/useFriends";

// Reusable "start a new match" flow (vs random / vs friend). Extracted from
// DashboardPage so the same flow powers both the dashboard action centre and
// the global "New match" button/modal in the nav — one implementation, no
// duplicated state machine. Navigates to /match/:id on launch.

const PHASE = { IDLE: "idle", RANDOM_CONFIRM: "random_confirm", FRIEND_PICK: "friend_pick", FRIEND_CONFIRM: "friend_confirm" };

function FriendPicker({ onSelect, onCancel }) {
  const { t } = useTranslation();
  const { friends, loading } = useFriends();
  const withTeam = friends.filter((f) => f.team_id);

  return (
    <div className="dashboard-friend-picker">
      <p className="dashboard-card-label">{t("dashboard.pickFriend")}</p>
      {loading && <p className="dashboard-loading">…</p>}
      {!loading && withTeam.length === 0 && (
        <p className="dashboard-friend-empty">{t("dashboard.noFriendsWithTeam")}</p>
      )}
      {withTeam.map((f) => (
        <button key={f.id} className="dashboard-friend-row" onClick={() => onSelect(f)}>
          {f.name}
        </button>
      ))}
      <button className="dashboard-cancel" onClick={onCancel}>{t("dashboard.cancel")}</button>
    </div>
  );
}

function FriendConfirmCard({ friend, onPlay, onBack, loading }) {
  const { t } = useTranslation();
  return (
    <>
      <p className="dashboard-card-label">{t("dashboard.challengeConfirm")}</p>
      <p className="dashboard-card-name">{friend.name}</p>
      <div className="dashboard-card-actions">
        <button className="btn-secondary" onClick={onBack} disabled={loading}>{t("dashboard.back")}</button>
        <button className="btn-primary"   onClick={onPlay} disabled={loading}>{loading ? "…" : t("dashboard.play")}</button>
      </div>
    </>
  );
}

function RandomConfirmCard({ onPlay, onBack, loading }) {
  const { t } = useTranslation();
  return (
    <>
      <p className="dashboard-card-label">{t("dashboard.randomConfirm")}</p>
      <div className="dashboard-card-actions">
        <button className="btn-secondary" onClick={onBack}  disabled={loading}>{t("dashboard.back")}</button>
        <button className="btn-primary"   onClick={onPlay}  disabled={loading}>{loading ? "…" : t("dashboard.play")}</button>
      </div>
    </>
  );
}

// readiness (optional): when provided, the choose buttons are disabled unless
// readiness.ready (dashboard behaviour). Omit it to always allow (a created
// team is playable). onLaunched fires right before navigation — used by the
// modal to close itself.
export default function NewMatchFlow({ readiness, onLaunched }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [phase, setPhase]                   = useState(PHASE.IDLE);
  const [error, setError]                   = useState(null);
  const [launching, setLaunching]           = useState(false);
  const [selectedFriend, setSelectedFriend] = useState(null);

  const ready = readiness ? readiness.ready : true;

  const launch = async (request) => {
    setLaunching(true);
    setError(null);
    try {
      const result = await request();
      onLaunched?.();
      navigate(`/match/${result.match_id}`);
    } catch (e) {
      setError(apiErrorMessage(e, t));
      setPhase(PHASE.IDLE);
    } finally {
      setLaunching(false);
    }
  };

  const handlePlayRandom      = () => launch(() => api("/game/random", { method: "POST" }));
  const handleChallengeFriend = () => launch(() => api(`/game/friendly/${selectedFriend.team_id}`, { method: "POST" }));

  return (
    <>
      {error && <p className="dashboard-error">{error}</p>}

      {phase === PHASE.IDLE && (
        <>
          <p className="dashboard-card-label">{t("dashboard.newMatch")}</p>
          <div className="dashboard-choosing">
            <button
              className="btn-primary"
              onClick={() => setPhase(PHASE.RANDOM_CONFIRM)}
              disabled={!ready}
              title={!ready ? t("dashboard.fixTeamTooltip") : undefined}
            >
              {t("dashboard.vsRandom")}
            </button>
            <button
              className="btn-primary"
              onClick={() => setPhase(PHASE.FRIEND_PICK)}
              disabled={!ready}
              title={!ready ? t("dashboard.fixTeamTooltip") : undefined}
            >
              {t("dashboard.vsFriend")}
            </button>
          </div>
        </>
      )}

      {phase === PHASE.RANDOM_CONFIRM && (
        <RandomConfirmCard onPlay={handlePlayRandom} onBack={() => setPhase(PHASE.IDLE)} loading={launching} />
      )}

      {phase === PHASE.FRIEND_PICK && (
        <FriendPicker
          onSelect={(f) => { setSelectedFriend(f); setPhase(PHASE.FRIEND_CONFIRM); }}
          onCancel={() => { setSelectedFriend(null); setPhase(PHASE.IDLE); }}
        />
      )}

      {phase === PHASE.FRIEND_CONFIRM && selectedFriend && (
        <FriendConfirmCard
          friend={selectedFriend}
          onPlay={handleChallengeFriend}
          onBack={() => { setSelectedFriend(null); setPhase(PHASE.IDLE); }}
          loading={launching}
        />
      )}
    </>
  );
}
